import type { PartnerLanguage } from "@/features/parties/partnerUsers.api";

// The people of a customer pasted into the invitation list (docs/partner-portal.md §15, T4 and T7): a column copied
// out of the export of the TALIX Portal, or typed. Per line in any order a name, an e-mail address, a language and
// when they last signed in on the TALIX Portal; a first column with the customer's code leaves out the lines of
// another customer, so the whole export can be pasted. Nothing of it is kept: only who is ticked gets an account.

export interface PastedPerson {
  name: string;
  email: string;
  language: PartnerLanguage | null;
  /** Last signed in on the TALIX Portal, when the list says. */
  lastLogin: Date | null;
}

export interface Pasted {
  people: PastedPerson[];
  /** Lines with the code of another customer. */
  otherCustomer: number;
  /** Lines without an e-mail address. */
  unreadable: number;
}

const LANGUAGES: Record<string, PartnerLanguage> = {
  nl: "nl",
  nld: "nl",
  dut: "nl",
  ned: "nl",
  nederlands: "nl",
  dutch: "nl",
  en: "en",
  eng: "en",
  gb: "en",
  uk: "en",
  engels: "en",
  english: "en",
  de: "de",
  deu: "de",
  ger: "de",
  duits: "de",
  deutsch: "de",
  german: "de",
};

export function languageOf(text: string): PartnerLanguage | null {
  return LANGUAGES[text.trim().toLowerCase()] ?? null;
}

/** 2025-03-04, 2025-03-04 10:12:33.000 (SQL Server), 4-3-2025, 04/03/2025 14:08, 4-3-25. */
export function dateOf(text: string): Date | null {
  const value = text.trim();
  let y: number, m: number, d: number;
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T].*)?$/.exec(value);
  const dutch = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2}|\d{4})(?:[ T].*)?$/.exec(value);
  if (iso) {
    [y, m, d] = [Number(iso[1]), Number(iso[2]), Number(iso[3])];
  } else if (dutch) {
    [d, m, y] = [Number(dutch[1]), Number(dutch[2]), Number(dutch[3])];
    if (y < 100) y += 2000;
  } else {
    return null;
  }
  const date = new Date(y, m - 1, d);
  return date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d ? date : null;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** A relation's code as TALIS and AFAS give it: VR10027. */
const CODE = /^[a-z]{2}\d{4,}$/i;

export function isEmail(text: string): boolean {
  return EMAIL.test(text.trim());
}

/** An address of Willems itself: nobody of our own signs in on the customer portal for a customer. */
export function isWillems(email: string): boolean {
  return /@(?:[a-z0-9-]+\.)*willemslogistics\.(?:com|nl)$/i.test(email.trim());
}

/** Not signed in on the TALIX Portal for more than half a year: left unticked. */
export function isStale(lastLogin: Date | null, now = new Date()): boolean {
  if (!lastLogin) return false;
  const halfYear = new Date(now);
  halfYear.setMonth(halfYear.getMonth() - 6);
  return lastLogin < halfYear;
}

function fieldsOf(line: string): string[] {
  const separator = line.includes("\t") ? "\t" : line.includes(";") ? ";" : ",";
  return line
    .split(separator)
    .map((field) => field.trim())
    .filter(Boolean);
}

export function parsePeople(text: string, partyCode: string): Pasted {
  const pasted: Pasted = { people: [], otherCustomer: 0, unreadable: 0 };
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line || line.startsWith("#")) continue;
    let fields = fieldsOf(line);
    const email = fields.find(isEmail);
    if (!email) {
      // A row of column names is no person; anything else that names nobody is.
      if (!fields.some((field) => /e-?mail/i.test(field))) pasted.unreadable += 1;
      continue;
    }
    const code = fields.find((field) => CODE.test(field));
    if (code && code.toUpperCase() !== partyCode.toUpperCase()) {
      pasted.otherCustomer += 1;
      continue;
    }
    fields = fields.filter((field) => field !== code && field !== email);
    const language = fields.map(languageOf).find((value) => value !== null) ?? null;
    const lastLogin = fields.map(dateOf).find((value) => value !== null) ?? null;
    const name = fields.find((field) => languageOf(field) === null && dateOf(field) === null) ?? "";
    pasted.people.push({ name, email: email.toLowerCase(), language, lastLogin });
  }
  return pasted;
}
